import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useCreateChannelConnection,
  useTestConnection,
  useUpsertChannelMapping,
  useActivateChannel,
  useDisableChannel,
  useSyncChannel,
  bookingComMappingDetailsQuery,
  apartmentChannelEventsQuery,
  channelConnectionsByProviderQuery,
} from '@/lib/channels';
import type { ApartmentChannelMapping, ChannelConnection } from '@/lib/channels';
import { messageFromError } from '@/lib/api';
import { useToast } from '@/components/ToastProvider';
import { useConfirm } from '@/components/DialogProvider';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ReadOnlyValue } from './ReadOnlyValue';

interface Props {
  apartmentID: string;
  mapping: ApartmentChannelMapping | undefined;
  canWrite: boolean;
}

// Phase 1: create a channel connection via hotel_id, fetch mapping details,
// then map our room_type / rate_plan to OTA IDs.

export function BookingComConnect({ apartmentID, mapping, canWrite }: Props) {
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const [err, setErr] = useState<string | null>(null);

  // Existing connections for booking_com
  const { data: connections = [] } = useQuery(
    channelConnectionsByProviderQuery('booking_com'),
  );

  // New-connection form state (declared before activeConn so a connection just
  // created in this session resolves immediately).
  const [hotelId, setHotelId] = useState('');
  const [connLabel, setConnLabel] = useState('Booking.com');
  const [createdConnId, setCreatedConnId] = useState<string | null>(null);

  // Booking.com connections are PER-APARTMENT: each apartment maps to its own
  // hotel_id → its own Channex property/channel. Scope the global list to THIS
  // apartment so another apartment's connection never leaks in and hides the
  // "new connection" form (unlike Airbnb, where one OAuth account is shared
  // across apartments). A connection belongs to this apartment when it is
  // anchored here, linked by this apartment's mapping, or was just created in
  // this session.
  const linkedConnectionId = mapping?.connection_id;
  const aptConnections = connections.filter(
    (c) =>
      c.anchor_apartment_id === apartmentID ||
      c.id === linkedConnectionId ||
      c.id === createdConnId,
  );
  const activeConn: ChannelConnection | undefined =
    aptConnections.find((c) => c.id === (linkedConnectionId ?? createdConnId)) ??
    aptConnections[0];

  // Mutation hooks
  const createConn = useCreateChannelConnection();
  const testConn = useTestConnection();
  const upsertMapping = useUpsertChannelMapping(apartmentID);
  const activate = useActivateChannel(apartmentID);
  const disable = useDisableChannel(apartmentID);
  const sync = useSyncChannel(apartmentID);

  // After connection is created / identified, fetch mapping details
  const mappingConnId = createdConnId ?? activeConn?.id;
  const { data: mappingDetails } = useQuery({
    ...bookingComMappingDetailsQuery(mappingConnId ?? ''),
    enabled: !!mappingConnId,
  });

  // Mapping selects
  const [selectedRoomId, setSelectedRoomId] = useState<string>(
    mapping?.external_room_id ?? '',
  );
  const [selectedRateId, setSelectedRateId] = useState<string>(
    mapping?.external_rate_id ?? '',
  );

  // Recent errors
  const { data: events = [] } = useQuery(
    apartmentChannelEventsQuery(apartmentID, 'booking_com'),
  );
  const recentErrors = events.filter((e) => e.severity === 'error').slice(0, 5);

  // --- Handlers ---

  async function handleTest() {
    setErr(null);
    if (!hotelId.trim()) {
      setErr('Enter your Booking.com hotel ID first.');
      return;
    }
    try {
      const result = await createConn.mutateAsync({
        provider: 'booking_com',
        label: connLabel.trim() || 'Booking.com',
        anchor_apartment_id: apartmentID,
        hotel_id: hotelId.trim(),
      });
      const conn = result.data as ChannelConnection;
      setCreatedConnId(conn.id);
      toast({ kind: 'success', title: 'Connection created', message: `Status: ${conn.status}` });
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  async function handleMap() {
    setErr(null);
    const connId = createdConnId ?? activeConn?.id;
    if (!connId) {
      setErr('No connection available. Run "Test" first.');
      return;
    }
    if (!selectedRoomId || !selectedRateId) {
      setErr('Select both a room type and a rate plan.');
      return;
    }
    const selectedRate = selectedRoom?.rates.find(
      (r) => String(r.id) === selectedRateId,
    );
    try {
      await upsertMapping.mutateAsync({
        provider: 'booking_com',
        body: {
          connection_id: connId,
          external_room_id: selectedRoomId,
          external_rate_id: selectedRateId,
          room_type_code: Number(selectedRoomId),
          rate_plan_code: Number(selectedRateId),
          occupancy: selectedRate?.occupancies[0] ?? 2,
          pricing_type: mappingDetails?.pricing_type,
          primary_occ: true,
        },
      });
      toast({ kind: 'success', title: 'Booking.com mapping saved' });
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  async function handleActivate() {
    setErr(null);
    try {
      await activate.mutateAsync('booking_com');
      toast({ kind: 'success', title: 'Booking.com channel activated' });
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  async function handleDisable() {
    const ok = await askConfirm({
      title: 'Disable Booking.com channel?',
      body: 'ARI pushes will stop. You can re-enable at any time.',
      destructive: true,
      confirmLabel: 'Disable',
    });
    if (!ok) return;
    setErr(null);
    try {
      await disable.mutateAsync('booking_com');
      toast({ kind: 'success', title: 'Booking.com channel disabled' });
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  async function handleSync() {
    setErr(null);
    try {
      await sync.mutateAsync('booking_com');
      toast({ kind: 'success', title: 'Sync initiated' });
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  async function handleTestExisting() {
    if (!activeConn) return;
    setErr(null);
    try {
      const result = await testConn.mutateAsync(activeConn.id);
      const conn = result.data as ChannelConnection;
      toast({ kind: 'success', title: 'Test passed', message: `Status: ${conn.status}` });
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  // Derive room/rate options
  const roomOptions = (mappingDetails?.rooms ?? []).map((r) => ({
    value: String(r.id),
    label: `${r.title} (${r.id})`,
  }));

  const selectedRoom = mappingDetails?.rooms.find(
    (r) => String(r.id) === selectedRoomId,
  );
  const rateOptions = (selectedRoom?.rates ?? []).map((r) => ({
    value: String(r.id),
    label: `${r.title} (${r.id})`,
  }));

  const isConnected = !!activeConn;
  const hasMappingDetails = !!mappingDetails;

  return (
    <div className="space-y-5">
      {/* Connection status */}
      {isConnected && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted">Connection:</span>
          <span className="truncate text-sm font-medium">{activeConn.label}</span>
          <ConnectionStatusBadge status={activeConn.status} />
        </div>
      )}

      {/* New connection form — shown when no active connection. A read-only
          caller gets the explanation without the controls: the inputs here are
          empty new-connection state, so leaving them rendered offered nothing
          to read and a form that could never be submitted. */}
      {!isConnected && (
        <div className="space-y-3 rounded-[var(--radius-control)] border border-[var(--border-soft)] bg-[var(--surface-sunken)] p-4">
          {canWrite ? (
            <>
              <p className="text-sm text-muted">
                Enter your Booking.com hotel ID. You can find it in the Booking.com
                extranet under{' '}
                <strong>Account → Connectivity</strong>.
              </p>
              <Input
                label="Hotel ID"
                id="bcom-hotel-id"
                type="text"
                value={hotelId}
                onChange={(e) => setHotelId((e.target as HTMLInputElement).value)}
                placeholder="e.g. 1234567"
              />
              <Input
                label="Label (internal)"
                id="bcom-label"
                type="text"
                value={connLabel}
                onChange={(e) => setConnLabel((e.target as HTMLInputElement).value)}
              />
              <Button
                type="button"
                variant="primary"
                size="sm"
                loading={createConn.isPending}
                onClick={handleTest}
              >
                Test connection
              </Button>
            </>
          ) : (
            <p className="text-sm text-muted">
              Not connected. Connecting this channel needs edit access to the
              apartment.
            </p>
          )}
        </div>
      )}

      {/* Mapping details — shown after connection is resolved */}
      {(isConnected || createdConnId) && hasMappingDetails && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            Map room &amp; rate
          </h4>
          {/* Unlike the connection form above, these selects carry real
              information for a read-only caller — WHICH room and rate this
              apartment is mapped to. So the read-only path keeps the values
              and drops the controls, rather than hiding the block. */}
          {canWrite ? (
            <>
              <Select
                label="OTA room type"
                options={roomOptions}
                value={selectedRoomId}
                onChange={(e) => {
                  setSelectedRoomId(e.target.value);
                  setSelectedRateId('');
                }}
                placeholder="Select room type…"
              />
              <Select
                label="OTA rate plan"
                options={rateOptions}
                value={selectedRateId}
                onChange={(e) => setSelectedRateId(e.target.value)}
                placeholder="Select rate plan…"
                disabled={!selectedRoomId}
              />
              <Button
                type="button"
                variant="primary"
                size="sm"
                loading={upsertMapping.isPending}
                onClick={handleMap}
              >
                {mapping ? 'Update mapping' : 'Create mapping'}
              </Button>
            </>
          ) : (
            <>
              {/* Fall back to the bare id, not to `empty`: the label only
                  resolves once the mapping-details query lands, and until then
                  "Not mapped" would read as a determination about a channel
                  that IS mapped. An id names it honestly in the meantime. */}
              <ReadOnlyValue
                label="OTA room type"
                value={roomOptions.find((o) => o.value === selectedRoomId)?.label ?? selectedRoomId}
                empty="Not mapped"
              />
              <ReadOnlyValue
                label="OTA rate plan"
                value={rateOptions.find((o) => o.value === selectedRateId)?.label ?? selectedRateId}
                empty="Not mapped"
              />
            </>
          )}
        </div>
      )}

      {/* Actions for a mapped, connected channel */}
      {mapping && canWrite && (
        <div className="flex flex-wrap gap-2">
          {!mapping.enabled && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              loading={activate.isPending}
              onClick={handleActivate}
            >
              Activate
            </Button>
          )}
          {mapping.enabled && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              loading={disable.isPending}
              onClick={handleDisable}
            >
              Disable
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={sync.isPending}
            onClick={handleSync}
          >
            Sync
          </Button>
          {activeConn && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={testConn.isPending}
              onClick={handleTestExisting}
            >
              Test
            </Button>
          )}
        </div>
      )}

      {err && (
        <p role="alert" className="text-sm text-destructive">
          {err}
        </p>
      )}

      {/* Recent errors */}
      {recentErrors.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            Recent errors
          </h4>
          <ul className="space-y-1.5">
            {recentErrors.map((ev) => (
              <li
                key={ev.id}
                className="rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-3 py-2 text-xs text-destructive"
              >
                <span className="font-semibold">{ev.event_type}</span>
                {' · '}
                {new Date(ev.occurred_at).toLocaleString()}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ConnectionStatusBadge({ status }: { status: ChannelConnection['status'] }) {
  if (status === 'active') return <Badge variant="success" size="sm" dot>Active</Badge>;
  if (status === 'error') return <Badge variant="danger" size="sm">Error</Badge>;
  if (status === 'pending_auth') return <Badge variant="warning" size="sm">Pending auth</Badge>;
  return <Badge variant="neutral" size="sm">Inactive</Badge>;
}
